<script setup>
import { onMounted, ref, computed, watch } from 'vue';
import { useToggle } from '@vueuse/core';
import MetricCard from '../overview/MetricCard.vue';
import BaseHeatmap from './BaseHeatmap.vue';
import HeatmapDateRangeSelector from './HeatmapDateRangeSelector.vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useLiveRefresh } from 'dashboard/composables/useLiveRefresh';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import endOfDay from 'date-fns/endOfDay';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import startOfMonth from 'date-fns/startOfMonth';
import subDays from 'date-fns/subDays';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { useI18n } from 'vue-i18n';
import { formatDuration } from '../../helpers/durationFormatHelper';

const store = useStore();
const { t } = useI18n();

const uiFlags = useMapGetter('getOverviewUIFlags');
const heatmapData = useMapGetter('getAccountOnlineTimeHeatmapData');
const agents = useMapGetter('agents/getAgents');

const selectedFrom = ref(null);
const selectedTo = ref(null);
const selectedDaysBefore = ref(null);
const selectedAgent = ref(null);
const isMonthFilter = ref(false);
const currentMonthOffset = ref(0);

const selectedRange = computed(() => {
  if (!selectedFrom.value || !selectedTo.value) {
    return null;
  }
  return { from: selectedFrom.value, to: selectedTo.value };
});

const numberOfRows = computed(() => {
  if (!selectedRange.value) {
    return 0;
  }
  const dateDifference = differenceInCalendarDays(
    selectedRange.value.to,
    selectedRange.value.from
  );
  return dateDifference + 1;
});

const agentMenuItems = computed(() => {
  return [
    {
      label: t('OVERVIEW_REPORTS.ONLINE_TIME_HEATMAP.ALL_AGENTS'),
      value: null,
      action: 'select_agent',
    },
    ...agents.value.map(agent => ({
      label: agent.name,
      value: agent.id,
      action: 'select_agent',
    })),
  ];
});

const selectedAgentFilter = computed(() => {
  if (!selectedAgent.value) {
    return { label: t('OVERVIEW_REPORTS.ONLINE_TIME_HEATMAP.ALL_AGENTS') };
  }
  return agentMenuItems.value.find(
    item => item.value === selectedAgent.value.id
  );
});

const isLoading = computed(
  () => uiFlags.value.isFetchingAccountOnlineTimeHeatmap
);

// Keeps relative presets (last 7 days / this month) aligned with "now" during live refreshes.
const resolveActiveRange = () => {
  if (isMonthFilter.value && currentMonthOffset.value === 0) {
    const now = new Date();
    const monthStart = startOfMonth(now);
    return {
      from: startOfDay(monthStart),
      to: endOfDay(now),
    };
  }

  if (!isMonthFilter.value && selectedDaysBefore.value !== null) {
    const to = endOfDay(new Date());
    return {
      from: startOfDay(subDays(to, Number(selectedDaysBefore.value))),
      to,
    };
  }

  return selectedRange.value;
};

const [showAgentDropdown, toggleAgentDropdown] = useToggle();

const fetchHeatmapData = () => {
  if (isLoading.value) {
    return;
  }

  const range = resolveActiveRange();
  if (!range) {
    return;
  }

  const { from, to } = range;

  store.dispatch('fetchAccountOnlineTimeHeatmap', {
    from: getUnixTime(from),
    to: getUnixTime(to),
    agentId: selectedAgent.value?.id,
  });
};

const handleAgentAction = ({ value }) => {
  toggleAgentDropdown(false);
  selectedAgent.value = value
    ? agents.value.find(agent => agent.id === value)
    : null;
};

const { startRefetching } = useLiveRefresh(fetchHeatmapData);

const handleRangeTypeChange = type => {
  isMonthFilter.value = type === 'month';
};

const handleMonthOffsetChange = offset => {
  currentMonthOffset.value = offset;
};

const formatHeatmapDuration = seconds => formatDuration(seconds, t);

watch(
  () => [selectedFrom.value, selectedTo.value],
  ([from, to]) => {
    if (from && to) {
      fetchHeatmapData();
    }
  }
);

watch(
  () => selectedAgent.value,
  () => {
    if (selectedRange.value) {
      fetchHeatmapData();
    }
  }
);

onMounted(() => {
  store.dispatch('agents/get');
  startRefetching();
});
</script>

<template>
  <div class="flex flex-row flex-wrap max-w-full">
    <MetricCard :header="t('OVERVIEW_REPORTS.ONLINE_TIME_HEATMAP.HEADER')">
      <template #control>
        <HeatmapDateRangeSelector
          v-model:from="selectedFrom"
          v-model:to="selectedTo"
          v-model:days-num="selectedDaysBefore"
          @range-type-change="handleRangeTypeChange"
          @month-offset-change="handleMonthOffsetChange"
        />
        <div
          v-on-clickaway="() => toggleAgentDropdown(false)"
          class="relative flex items-center group"
        >
          <Button
            sm
            slate
            faded
            :label="selectedAgentFilter.label"
            class="rounded-md group-hover:bg-n-alpha-2 max-w-[200px]"
            @click="toggleAgentDropdown()"
          />
          <DropdownMenu
            v-if="showAgentDropdown"
            :menu-items="agentMenuItems"
            show-search
            :search-placeholder="
              t('OVERVIEW_REPORTS.ONLINE_TIME_HEATMAP.SEARCH_AGENT')
            "
            class="mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0 top-full !min-w-56 max-w-56 max-h-96"
            @action="handleAgentAction($event)"
          />
        </div>
      </template>
      <BaseHeatmap
        :heatmap-data="heatmapData"
        :number-of-rows="numberOfRows"
        :is-loading="isLoading"
        color-scheme="green"
        :format-tooltip-value="formatHeatmapDuration"
      />
    </MetricCard>
  </div>
</template>
